/**
 * leagueSpend — per-team position spend, and the two yardsticks it is read
 * against on the league page.
 *
 * 🔴 THREE NUMBERS, TWO ESTIMATORS, AND THE PAGE HAS TO SAY WHICH IS WHICH.
 * A row compares what THIS team spent at a position with (a) the median share
 * the other teams in this same auction spent there and (b) the real 2026
 * market. (a) and (b) are not the same kind of measurement and the view says
 * so out loud:
 *
 *   this team   its own dollars, exactly what the bar above draws
 *   this room   the MEDIAN across this room's teams of each team's own share,
 *               then multiplied by this room's budget so it reads in dollars
 *   the market  meta.marketPosShares from src/data/rateBenchmarks.json, which
 *               is POOLED DOLLARS across every charted 2026 room, likewise
 *               scaled to this room's budget
 *
 * 🔴 THE MARKET SHARES COME FROM THE ARTIFACT, NEVER FROM A LITERAL HERE.
 * They move on every harvest rerun (they are pinned in
 * scripts/build_rate_benchmarks.py and inventoried in
 * docs/reference/harvest-rerun-pin-inventory.md), so a constant copied into
 * this file would be a second, silently stale market. That is exactly what
 * REFERENCE_SPEND_SHARES in leagueMarket.js is NOT allowed to be used for
 * either: that constant is the Smithtown calibration baseline with a hand-sync
 * pact to batchSim, not the market.
 *
 * 🔴 FULL DENOMINATOR, same rule and same reason as leagueMarket.js's
 * buildMarketProfile: every priced dollar counts toward the denominator,
 * kickers and defenses included, so the four skill shares a caller renders sum
 * to slightly under 100%. That is honest, not a bug.
 *
 * 🔴 UNPRICED IS ABSENT, NOT ZERO. A room entered without dollars has no spend
 * breakdown at all; it does not have one made of zeroes. Same rule the gate
 * applies to a price nobody typed (see unpricedPicks.test.js) — a zero looks
 * like data and this one would look like a team that spent nothing.
 */

import benchmarks from "../data/rateBenchmarks.json";
import { splitRoster } from "./rateProjection.js";

/**
 * The positions the breakdown reports, in reading order.
 *
 * This is the ORDER and the ROW SET, never the denominator — see spendByPos.
 * It matches the six keys meta.marketPosShares carries.
 */
export const SPEND_POSITIONS = ["QB", "RB", "WR", "TE", "K", "DEF"];

function median(sorted) {
  if (!sorted.length) return null;
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

/**
 * One team's priced dollars, by position.
 *
 * Mirrors leagueMarket.js's spendByPos loop deliberately: an unlabelled
 * position falls into its own bucket rather than being dropped, so `total` is
 * every priced dollar and the segments a caller draws from `byPos` always sum
 * back to it. A pick with no price contributes nothing to either side — it is
 * not a $0 pick, it is a pick nobody priced.
 */
export function spendByPos(picks) {
  const byPos = {};
  let total = 0;
  for (const p of picks || []) {
    const paid = Number(p && p.paid);
    if (!Number.isFinite(paid) || paid <= 0) continue;
    const pos = (p && p.pos) || "—";
    byPos[pos] = (byPos[pos] || 0) + paid;
    total += paid;
  }
  return { byPos, total };
}

/**
 * The room's own yardstick: for each position, the MEDIAN across this room's
 * teams of that team's share of its own spend.
 *
 * Median, not pooled, and on purpose — this is the estimator that answers "what
 * did a typical seat in this room do", where a pooled figure would let the one
 * team that spent $180 on two running backs speak for the room. It is the same
 * median-across-rooms shape the trends page uses, one level down.
 *
 * A team that spent nothing at a position contributes a real 0 to that
 * position's list: it spent nothing there, which is data. A team with no priced
 * spend at all contributes nothing, because it has no shares to take a share of.
 */
export function roomMedianShares(teamPicksList) {
  const perTeam = (teamPicksList || []).map(spendByPos).filter((s) => s.total > 0);
  if (!perTeam.length) return null;
  const out = {};
  for (const pos of SPEND_POSITIONS) {
    const vals = perTeam.map((s) => (s.byPos[pos] || 0) / s.total).sort((a, b) => a - b);
    out[pos] = median(vals);
  }
  return out;
}

/**
 * The market yardstick, read straight off the committed benchmarks artifact.
 *
 * Returns null for a format the artifact has no cell for, and for an artifact
 * built before meta.marketPosShares existed, so an older cached artifact drops
 * the market column rather than rendering undefined dollars.
 */
export function marketPosShares(benchmarkFormat) {
  const cell = benchmarks[benchmarkFormat];
  const shares = cell && cell.meta && cell.meta.marketPosShares;
  return shares || null;
}

/**
 * Everything one team's card needs, or null when there is nothing to draw.
 *
 * `roomShares` is passed in rather than derived here because it is a property
 * of the ROOM: the caller computes it once over every team and hands the same
 * object to every card, so two cards in one report can never disagree about
 * what the room did.
 *
 * @param picks            this team's graded picks (pos + paid)
 * @param roomShares       roomMedianShares over the whole room, or null
 * @param benchmarkFormat  "superflex" | "1qb", the report's own cell key
 * @param budget           this league's budget, for scaling shares to dollars
 * @param hasPrices        false for a roster entered without dollars
 */
export function teamSpendBreakdown({ picks, roomShares, benchmarkFormat, budget, hasPrices }) {
  // Explicit false only. A caller with no opinion (the Sleeper league report,
  // where an auction always carries prices) leaves it undefined and falls
  // through to the totals test below, which reaches the same answer.
  if (hasPrices === false) return null;
  const { byPos, total } = spendByPos(picks);
  if (total <= 0) return null;

  const market = marketPosShares(benchmarkFormat);
  const scalable = Number.isFinite(budget) && budget > 0;
  const dollarsOf = (shares, pos) =>
    shares && scalable && Number.isFinite(shares[pos]) ? shares[pos] * budget : null;

  const rows = SPEND_POSITIONS.map((pos) => ({
    pos,
    dollars: byPos[pos] || 0,
    share: (byPos[pos] || 0) / total,
    roomDollars: dollarsOf(roomShares, pos),
    marketDollars: dollarsOf(market, pos),
  }));

  // Segments come off byPos, not off SPEND_POSITIONS, so the bar sums to every
  // priced dollar even if a pick ever arrives at a position the six do not
  // name. Known positions keep their reading order; anything else lands after
  // them in a stable one.
  const extra = Object.keys(byPos)
    .filter((pos) => !SPEND_POSITIONS.includes(pos))
    .sort();
  const segments = [...SPEND_POSITIONS, ...extra]
    .filter((pos) => (byPos[pos] || 0) > 0)
    .map((pos) => ({ pos, dollars: byPos[pos], share: byPos[pos] / total }));

  return { total, rows, segments, hasMarket: !!market };
}

/**
 * How this team's money split between the lineup it starts and the bench.
 *
 * 🔴 THE DENOMINATOR IS WHAT THE TEAM SPENT, NOT THE BUDGET, and the reason is
 * that the two halves are a PARTITION of one pile: every priced pick is either
 * started or benched, so starters + bench is exactly the spend and the two
 * percentages sum to 100. Against the budget they would sum to less, and the
 * gap would silently be leftover money — a third fact this line does not make,
 * and one a reader would have to guess at. It is also the denominator the spend
 * bar directly above it already uses (teamSpendBreakdown's `total`), so the two
 * lines are read against the same number rather than two.
 *
 * 🔴 WHO STARTS COMES FROM splitRoster AND NOWHERE ELSE. That is the one
 * definition of the filled lineup — greedy on projected ppg, every named
 * position before any flex, SUPER_FLEX last because it accepts everything — so
 * a FLEX takes the better projected of the eligible and this line cannot
 * disagree with the starter and bench CARDS drawn from the same call.
 *
 * 🔴 NO MARKET CLAIM IS MADE HERE, deliberately. The corpus carries no
 * starter/bench distribution, so there is nothing to compare this split
 * against and the line says only what THIS team did. A "vs real rooms" clause
 * would need a corpus recompute first.
 *
 * 🔴 ABSENT, NEVER ZEROED, on three separate grounds: a roster typed without
 * dollars, a league that sent no scoring (no lens, so no lineup was ever
 * filled), and a roster none of whose players the projection set matched. All
 * three reach the same answer — null — because in none of them is there a
 * split to report, and a 0%/100% would read as a team that benched everything.
 *
 * @param picks     this team's graded picks (pos + paid)
 * @param proj      projectTeam output for the same team, under the display lens
 * @param hasPrices false for a roster entered without dollars
 * @returns {{starterSpend, benchSpend, total, starterShare, benchShare}|null}
 */
export function starterBenchSpend({ picks, proj, hasPrices }) {
  // Explicit false only, the same contract teamSpendBreakdown uses.
  if (hasPrices === false) return null;
  const starters = (proj && proj.starters) || [];
  if (starters.length === 0) return null;

  const paidOf = (p) => {
    const v = Number(p && p.paid);
    return Number.isFinite(v) && v > 0 ? v : 0;
  };
  const total = (picks || []).reduce((a, p) => a + paidOf(p), 0);
  if (total <= 0) return null;

  const { starterRows, benchRows } = splitRoster(picks, proj);
  const sum = (rowsMap) =>
    [...rowsMap.values()].reduce((a, rows) => a + rows.reduce((b, r) => b + paidOf(r), 0), 0);

  const starterSpend = sum(starterRows);
  const benchSpend = sum(benchRows);
  return {
    starterSpend,
    benchSpend,
    total,
    starterShare: starterSpend / total,
    benchShare: benchSpend / total,
  };
}
